import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, Trash2, Pin, Target, TrendingUp, TrendingDown, BadgeCheck, Flame, Clock, Eye } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";

const REACTIONS = [
  { key: "like", emoji: "👍", label: "Like" },
  { key: "dislike", emoji: "👎", label: "Dislike" },
  { key: "laugh", emoji: "😂", label: "Laugh" },
  { key: "rocket", emoji: "🚀", label: "Rocket" },
  { key: "fire", emoji: "🔥", label: "Fire" },
];

function AuthorAccuracy({ authorId, isPrediction }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isPrediction || !authorId) return;
    api.get("/community/predictions/user/" + authorId)
      .then((r) => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authorId, isPrediction]);

  if (!isPrediction) return null;
  if (loading) return null;
  if (!stats || stats.total === 0) return (
    <span className="text-xs font-mono px-2 py-0.5 border border-[var(--border-soft)] text-[var(--text-tertiary)]">
      No resolved predictions
    </span>
  );

  return (
    <>
      <span className={"text-xs font-mono px-2 py-0.5 border " +
        (stats.accuracy >= 60 ? "text-green-400 border-green-400/30 bg-green-400/10" :
         stats.accuracy >= 40 ? "text-yellow-400 border-yellow-400/30 bg-yellow-400/10" :
         "text-red-400 border-red-400/30 bg-red-400/10")}>
        {stats.total === 0 || stats.correct === 0 ? "0%" : stats.accuracy + "%"} · {stats.correct}/{stats.total}
      </span>
      {stats.verified && (
        <span className="flex items-center gap-1 text-xs font-mono px-2 py-0.5 border border-yellow-400/40 bg-yellow-400/10 text-yellow-400" title="Verified Analyst: 70%+ accuracy on 10+ predictions">
          <BadgeCheck className="w-3 h-3" /> Verified Analyst
        </span>
      )}
      {stats.streak >= 3 && (
        <span className="flex items-center gap-1 text-xs font-mono px-2 py-0.5 border border-orange-400/40 bg-orange-400/10 text-orange-400">
          <Flame className="w-3 h-3" /> {stats.streak} Streak
        </span>
      )}
    </>
  );
}

function Countdown({ targetDate }) {
  const [timeLeft, setTimeLeft] = useState(null);

  useEffect(() => {
    const calc = () => {
      const diff = new Date(targetDate + "T23:59:59").getTime() - Date.now();
      if (diff <= 0) return null;
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      return { hours, minutes };
    };
    setTimeLeft(calc());
    const interval = setInterval(() => setTimeLeft(calc()), 60000);
    return () => clearInterval(interval);
  }, [targetDate]);

  if (!timeLeft) return null;

  return (
    <span className="flex items-center gap-1 text-xs font-mono text-yellow-400 border border-yellow-400/30 bg-yellow-400/5 px-3 py-1">
      <Clock className="w-3 h-3" />
      Resolves in {timeLeft.hours}h {timeLeft.minutes}m
    </span>
  );
}

export default function CommunityPostCard({ post, onChanged }) {
  const { user } = useAuth();
  const [reactionCounts, setReactionCounts] = useState({});
  const [userReaction, setUserReaction] = useState(null);
  const [showReactions, setShowReactions] = useState(false);
  const reactionRef = React.useRef(null);



  useEffect(() => {
    api.get("/community/posts/" + post.id + "/reactions")
      .then((r) => {
        setReactionCounts(r.data.counts || {});
        setUserReaction(r.data.user_reaction || null);
      })
      .catch(() => {});
  }, [post.id]);

  useEffect(() => {
    const handleClick = (e) => {
      if (reactionRef.current && !reactionRef.current.contains(e.target)) setShowReactions(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const react = async (reactionKey) => {
    if (!user) return toast.error("Sign in to react");
    try {
      const { data } = await api.post("/community/posts/" + post.id + "/react", { reaction: reactionKey });
      setReactionCounts(data.counts || {});
      setUserReaction(data.user_reaction);
      setShowReactions(false);
    } catch (_) { toast.error("Could not react"); }
  };

  const pin = async (e) => {
    e.preventDefault();
    try {
      if (post.pinned) {
        await api.post("/community/posts/" + post.id + "/unpin");
        toast.success("Post unpinned");
      } else {
        await api.post("/community/posts/" + post.id + "/pin");
        toast.success("Post pinned");
      }
      onChanged && onChanged();
    } catch (_) { toast.error("Could not pin post"); }
  };

  const remove = async (e) => {
    e.preventDefault();
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete("/community/posts/" + post.id);
      toast.success("Post deleted");
      onChanged && onChanged();
    } catch (_) { toast.error("Could not delete post"); }
  };

  const totalReactions = Object.values(reactionCounts).reduce((a, b) => a + b, 0);
  const canDelete = user && ((user.id === post.author_id && !post.is_prediction) || user.role === "admin");
  const isAdmin = user?.role === "admin";
  const isBull = post.direction === "above";

  return (
    <article
      data-testid={"community-post-" + post.id}
      className={"bg-[var(--bg-panel)] border transition " +
        (post.pinned ? "border-yellow-400/40" :
         post.is_prediction ? (post.resolved ? (post.correct ? "border-green-400/20 hover:border-green-400/40" : "border-red-400/20 hover:border-red-400/40") : "border-yellow-400/20 hover:border-yellow-400/40") :
         "border-[var(--border-soft)] hover:border-yellow-400/20")}
    >
      {/* Prediction header bar */}
      {post.is_prediction && (
        <div className={"flex items-center justify-between px-6 py-3 border-b flex-wrap gap-2 " +
          (post.resolved
            ? (post.correct ? "bg-green-400/5 border-green-400/20" : "bg-red-400/5 border-red-400/20")
            : "bg-yellow-400/5 border-yellow-400/20")}>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="flex items-center gap-1.5 text-xs font-mono font-bold uppercase tracking-widest text-yellow-400">
              <Target className="w-3 h-3" /> Price Prediction
            </span>
            <span className={"flex items-center gap-1 text-xs font-mono font-bold " + (isBull ? "text-green-400" : "text-red-400")}>
              {isBull ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {post.asset} {post.direction} ${Number(post.target_price).toLocaleString()}
            </span>
            <span className="text-xs font-mono text-[var(--text-tertiary)]">
              by {new Date(post.target_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </span>
          </div>
          {post.resolved ? (
            <span className={"text-xs font-mono font-bold px-3 py-1 border " +
              (post.correct
                ? "text-green-400 border-green-400/30 bg-green-400/10"
                : "text-red-400 border-red-400/30 bg-red-400/10")}>
              {post.correct ? "✓ Correct" : "✗ Wrong"}
            </span>
          ) : (
            <Countdown targetDate={post.target_date} />
          )}
        </div>
      )}

              {post.is_prediction && !post.resolved && post.current_price_hint && (
        <div className="px-6 pt-4">
          <div className="relative h-2 bg-[var(--bg-elevated)] rounded-full overflow-hidden">
            <div className="absolute left-1/2 top-0 h-full w-0.5 bg-[var(--text-tertiary)]/40" />
            <div className={"absolute top-0 h-full rounded-full " + ((post.progress_pct ?? 50) >= 50 ? "bg-green-500/50" : "bg-red-500/50")}
              style={
                (post.progress_pct ?? 50) >= 50
                  ? { left: "50%", width: (Math.min(100, post.progress_pct ?? 50) - 50) + "%" }
                  : { right: "50%", width: (50 - Math.max(0, post.progress_pct ?? 50)) + "%" }
              } />
          </div>
          <div className="flex justify-between text-xs font-mono text-[var(--text-tertiary)] mt-1">
            <span>started ${Number(post.price_at_prediction || post.current_price_hint).toLocaleString()}</span>
            <span className={(post.current_price_hint >= (post.price_at_prediction || post.current_price_hint)) ? "text-green-400" : "text-red-400"}>
              now ${Number(post.current_price_hint).toLocaleString()}
            </span>
          </div>
        </div>
      )}

      <div className="p-6">
        {post.pinned && (
          <div className="flex items-center gap-1 text-yellow-400 text-xs font-mono uppercase tracking-widest mb-3">
            <Pin className="w-3 h-3" /> Pinned
          </div>
        )}

        <div className="flex items-center gap-3 mb-4">
          <Link to={"/profile/" + post.author_id} className="flex-shrink-0 hover:opacity-80 transition">
  {post.author_avatar ? (
    <img src={post.author_avatar} alt={post.author_name} className="w-10 h-10 rounded-full object-cover" />
  ) : (
    <div className={"w-10 h-10 font-bold flex items-center justify-center text-sm rounded-full " +
      (post.is_prediction ? (isBull ? "bg-green-400 text-black" : "bg-red-400 text-black") : "bg-yellow-400 text-black")}>
      {(post.author_name || "U")[0].toUpperCase()}
    </div>
  )}
</Link>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <Link to={"/profile/" + post.author_id} className="text-sm font-medium text-[var(--text-primary)] hover:text-yellow-400 transition">
  {post.author_name}
</Link>
              <AuthorAccuracy authorId={post.author_id} isPrediction={post.is_prediction} />
            </div>
            <div className="text-xs font-mono text-[var(--text-tertiary)]">
              {new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {isAdmin && (
              <button onClick={pin} className={"transition " + (post.pinned ? "text-yellow-400" : "text-[var(--text-tertiary)] hover:text-yellow-400")} title={post.pinned ? "Unpin post" : "Pin post"}>
                <Pin className="w-4 h-4" />
              </button>
            )}
            {canDelete && (
              <button onClick={remove} data-testid={"community-post-delete-" + post.id} className="text-[var(--text-tertiary)] hover:text-red-400 transition" title="Delete post">
                <Trash2 className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        <Link to={"/community/" + post.id} className="block group">
          <div className="flex items-start gap-4">
            {post.image_url && (
              <img
                src={post.image_url}
                alt=""
                loading="lazy"
                onError={(e) => { e.target.style.display = "none"; }}
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-xl object-cover border border-[var(--border-soft)] flex-shrink-0"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h3 className="font-display font-bold text-xl text-[var(--text-primary)] group-hover:text-yellow-400 transition leading-snug">
                  {post.title}
                </h3>
                {post.sentiment && (
                  <span className={"flex-shrink-0 text-xs font-bold uppercase tracking-wide px-2 py-0.5 rounded-full border " +
                    (post.sentiment === "bullish" ? "border-green-500 text-green-400 bg-green-500/10" :
                     post.sentiment === "bearish" ? "border-red-500 text-red-400 bg-red-500/10" :
                     "border-[var(--border-soft)] text-[var(--text-tertiary)]")}>
                    {post.sentiment}
                  </span>
                )}
              </div>
              <p className="mt-2 text-[var(--text-secondary)] leading-relaxed line-clamp-4 whitespace-pre-wrap break-all">
                {post.content}
              </p>
              {post.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {post.tags.map((t) => (
                    <span key={t} className="text-xs font-mono text-yellow-400/80">#{t}</span>
                  ))}
                </div>
              )}
            </div>
            </div>
        </Link>

        <div className="mt-5 pt-4 border-t border-[var(--border-soft)] flex items-center gap-4 text-sm flex-wrap">
          <div ref={reactionRef} className="relative">
            <button
              onClick={() => setShowReactions(!showReactions)}
              className={"flex items-center gap-2 transition-all duration-200 px-3 py-1.5 border rounded-full text-xs " +
                (userReaction ? "border-yellow-400/50 text-yellow-400 bg-yellow-400/10 shadow-[0_0_12px_rgba(250,204,21,0.15)]" : "border-[var(--border-soft)] text-[var(--text-tertiary)] hover:border-yellow-400/40 hover:text-[var(--text-primary)] hover:scale-105")}
            >
              <span className="text-base">{userReaction ? REACTIONS.find((r) => r.key === userReaction)?.emoji : "😊"}</span>
              <span className="font-mono">{totalReactions > 0 ? totalReactions : "React"}</span>
            </button>
            {showReactions && (
              <div
                className="absolute bottom-full left-0 mb-2 bg-[var(--bg-elevated)]/95 backdrop-blur-md border border-[var(--border-soft)] rounded-full px-3 py-2 flex items-center gap-1.5 z-10 shadow-2xl"
                style={{ animation: "popIn 0.18s ease-out" }}
              >
                {REACTIONS.map((r) => (
                  <button key={r.key} onClick={() => react(r.key)} title={r.label}
                    className={"text-xl transition-all duration-150 hover:-translate-y-1.5 hover:scale-125 " + (userReaction === r.key ? "opacity-100 scale-125 drop-shadow-[0_0_8px_rgba(250,204,21,0.5)]" : "opacity-70 hover:opacity-100")}>
                    {r.emoji}
                  </button>
                ))}
              </div>
            )}
            <style>{`@keyframes popIn { 0% { opacity: 0; transform: scale(0.8) translateY(4px); } 100% { opacity: 1; transform: scale(1) translateY(0); } }`}</style>
          </div>

          {Object.entries(reactionCounts).map(([key, count]) => {
            if (!count) return null;
            const reaction = REACTIONS.find((r) => r.key === key);
            if (!reaction) return null;
            return (
              <span key={key} className="text-xs font-mono text-[var(--text-tertiary)] flex items-center gap-1">
                {reaction.emoji} {count}
              </span>
            );
          })}

          
          <Link to={"/community/" + post.id} data-testid={"community-post-comment-link-" + post.id} className="flex items-center gap-2 text-[var(--text-tertiary)] hover:text-yellow-400 transition ml-auto">
            <MessageCircle className="w-4 h-4" />
            <span className="font-mono">{post.comments_count || 0}</span>
          </Link>
        </div>
      </div>
    </article>
  );
}